/**
 * Pure permission helpers shared between the auth store and any
 * component that wants to gate UI on the current user's effective
 * Work Role operations.
 *
 * Lifted out of `stores/authStore.js` so they can be unit-tested
 * without the store's side-effect imports (axios, socket.io, etc.).
 * The store exposes thin wrappers (`useAuthStore.hasOperation(key)`)
 * that read its `user` state and delegate here.
 *
 * Resolution rules — must stay in lock-step with the backend
 * (`utils/permissions.js` on the API side):
 *
 *   - `null` / missing user → false (caller hasn't bootstrapped yet).
 *   - `role: 'company_admin'` or `'super_admin'` → true (org / platform
 *     admins always pass operation checks).
 *   - Anyone else → match against the `operations` array on the user.
 *
 * The user passed in is the shape produced by `buildUser` in the
 * auth store: `{ id, role, workRole, workRoleId, workRoleName,
 * workRoleLevel, operations, ... }`. Defensive against a missing
 * `operations` field — older payloads (or a frontend deployed before
 * the backend update) get the safe "no extra permissions" path.
 */

/** True if `user` can perform operation `key`. */
export function userHasOperation(user, key) {
  if (!user) return false
  if (
    user.role === 'company_admin' ||
    user.role === 'super_admin' ||
    user.role === 'sub_admin'
  )
    return true
  const ops = user.operations
  return Array.isArray(ops) && ops.includes(key)
}

/** True if `user` holds AT LEAST ONE of the operation keys in `keys`. */
export function userHasAnyOperation(user, keys) {
  if (!Array.isArray(keys) || keys.length === 0) return false
  if (!user) return false
  if (
    user.role === 'company_admin' ||
    user.role === 'super_admin' ||
    user.role === 'sub_admin'
  )
    return true
  const ops = user.operations
  if (!Array.isArray(ops)) return false
  for (const k of keys) if (ops.includes(k)) return true
  return false
}
